import {
    ModalBody, ModalHeader, Modal, ModalContent,
    ModalOverlay,
} from '@chakra-ui/react';
import { ReportedFindingsMachineContext } from './machine/context';

import { ReportedFindingsWizardHeader } from './components/table/reported-findings-wizard-header';
import { ResolveFindingSummaryScreen } from './screens/resolve-finding-summary-screen';
import { DismissFindingModalUI } from '../../../property-details/reported-findings-tab/dismiss-reported-finding-button';
import { ReportedFindingsWizardSkeleton } from './components/table/reported-findings-wizard-table';
import { RequestQuoteWizardScreen } from './screens/request-quote-wizard-screen';
import { ForwardQuoteWizardScreen } from './screens/forward-quote-wizard-screen';
import { ResolvedByThirdPartyScreen } from './screens/resolved-by-third-party-screen';
import { ExceptionHandlingReportedFinding } from './components/exception-handling-reported-finding';
import { InformDIScreen } from './screens/inform-di-screen';
import { EnterBuiltYearScreen } from './screens/enter-built-year-screen';

function FatalErrorScreen() {
    const machine = ReportedFindingsMachineContext.useActorRef();
    const message = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.fatalErrorMessage
    );
    const onClose = () => machine.send({ type: 'FORCE_CLOSE_MODAL' });
    const errorDescription = message === null ? 'Please try again later or contact support.' : `Please try again later or contact support. (${message})`;
    return (
        <ExceptionHandlingReportedFinding
            title="An unexpected error occurred while resolving reported findings."
            description={errorDescription}
            onClose={onClose}
        />
    );
}

function NoReportedFindingsScreen() {
    const machine = ReportedFindingsMachineContext.useActorRef();
    const onClose = () => machine.send({ type: 'FORCE_CLOSE_MODAL' });

    return (
        <ExceptionHandlingReportedFinding
            title="There are currently no reported findings for this property."
            description="Please try again later or contact support."
            onClose={onClose}
        />
    );
}

export function ReportedFindingModal({
    compliancePropertyId,
}: {
    compliancePropertyId: number;
}) {
    const machine = ReportedFindingsMachineContext.useActorRef();
    const isOpen = ReportedFindingsMachineContext.useSelector(
        (state) => !state.matches('idle')
    );

    const isLoading = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('loadReportedFindings')
    );

    const isLandingPage = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('selectResolutionMethod')
                || state.matches('dismissFinding')
                || state.matches('forwardQuoteToLandlord')
    );

    const isRequestQuote = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('selectRequestQuoteItems')
            || state.matches('requestQuote')
            || state.matches('confirmRequestQuote')
            || state.matches('quoteRequestComplete')
    );
    const isThirdParty = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('selectThirdPartyItems')
            || state.matches('thirdPartyResolutionForm')
            || state.matches('thirdPartyDisclaimer')
            || state.matches('submitResolution')
            || state.matches('thirdPartyComplete')
    );

    const isForwardQuote = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('forwardQuoteToLandlord')
    );
    const isFatalError = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('fatalError')
    );
    const hasNoReportedFindings = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('noReportedFindings')
    );

    const isInformDI = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('informDI')
    );

    const isEnterBuiltYear = ReportedFindingsMachineContext.useSelector(
        (state) => state.matches('enterBuiltYear')
    );

    if (isForwardQuote) {
        return <ForwardQuoteWizardScreen />;
    }
    const hasError = isFatalError || hasNoReportedFindings;

    return (
        <Modal
            isOpen={isOpen}
            autoFocus={false}
            onClose={() => machine.send({ type: 'CLOSE_MODAL' })}
            size="4xl"
        >
            <ModalOverlay />
            <ModalContent>
                {(!hasError) && (
                    <ModalHeader>
                        <ReportedFindingsWizardHeader
                            compliancePropertyId={compliancePropertyId}
                        />
                    </ModalHeader>
                )}
                <ModalBody>
                    {isLoading && (<ReportedFindingsWizardSkeleton />)}
                    {isLandingPage && (<ResolveFindingSummaryScreen />)}
                    {isRequestQuote && (<RequestQuoteWizardScreen />)}
                    {isInformDI && (<InformDIScreen />)}
                    {isEnterBuiltYear && (<EnterBuiltYearScreen />)}
                    <DismissFindingModalUI />
                    {isThirdParty && (<ResolvedByThirdPartyScreen />)}
                    {isFatalError && (<FatalErrorScreen />)}
                    {hasNoReportedFindings && (<NoReportedFindingsScreen />)}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}
